import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Bell, CheckCheck, Megaphone, AlertTriangle, Info,
    Clock, FileText, ClipboardList, MessageCircle,
    ChevronRight, CheckCircle2, Radio,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';

// ── Helpers ────────────────────────────────────────────────────────────────

const formatRelTime = (iso) => {
    if (!iso) return '';
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1)  return 'Adesso';
    if (m < 60) return `${m}min fa`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h fa`;
    return `${Math.floor(h / 24)}gg fa`;
};

const TYPE_ICON = {
    broadcast:       Megaphone,
    past_due:        AlertTriangle,
    new_sub:         Bell,
    payment_ok:      CheckCircle2,
    deadline:        Clock,
    new_contract:    FileText,
    request_pending: ClipboardList,
    message:         MessageCircle,
    cte_propagated:  Radio,
};

const PREVIEW_COUNT = 5; // Quante notifiche mostrare nel dropdown

/**
 * NotificationBell — campanella Facebook-style.
 *
 * - Click campanella → apre dropdown con ultime 5 notifiche
 * - Dropdown: lista compatta + bottone "Vedi tutte le notifiche →"
 * - Posizionamento: usa left (non right) per evitare clipping nella sidebar sinistra
 * - Portal su document.body per evitare overflow:hidden della sidebar
 */
export default function NotificationBell() {
    const { notifications, unread, markAllRead, markOneRead, dismiss,
            scheduleMarkAllRead, cancelScheduledMarkAllRead } = useNotifications();
    const navigate    = useNavigate();
    const [open, setOpen] = useState(false);
    const [pos, setPos]   = useState({ top: 0, left: 0 });
    const buttonRef    = useRef(null);
    const dropdownRef  = useRef(null);

    // Calcola posizione: allinea il dropdown a SINISTRA del bottone
    // e lo shifta a destra se serve per non uscire dal viewport
    const updatePos = useCallback(() => {
        if (!buttonRef.current) return;
        const rect = buttonRef.current.getBoundingClientRect();
        const dropW = 344;
        // Prova ad allineare con il bordo destro del bottone
        let left = rect.right - dropW;
        // Clamp: non uscire a sinistra né a destra
        left = Math.max(8, Math.min(left, window.innerWidth - dropW - 8));
        setPos({ top: rect.bottom + 8, left });
    }, []);

    const handleToggle = () => {
        if (!open) {
            updatePos();
            // Auto mark-as-read dopo 3s (annullabile se si chiude prima)
            if (unread > 0) scheduleMarkAllRead(3000);
        } else {
            // Dropdown chiuso manualmente — cancella timer
            cancelScheduledMarkAllRead();
        }
        setOpen(v => !v);
    };

    // Chiudi cliccando fuori
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (
                buttonRef.current?.contains(e.target) ||
                dropdownRef.current?.contains(e.target)
            ) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        window.addEventListener('resize', updatePos);
        return () => window.removeEventListener('resize', updatePos);
    }, [open, updatePos]);

    const preview = notifications.slice(0, PREVIEW_COUNT);

    const dropdown = (
        <motion.div
            ref={dropdownRef}
            initial={{ opacity: 0, y: -10, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.96 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            style={{
                position:  'fixed',
                top:       pos.top,
                left:      pos.left,
                width:     344,
                zIndex:    9999,
                background: 'linear-gradient(160deg, #1e1a3a 0%, #13101f 100%)',
                border:     '1px solid rgba(255,255,255,0.1)',
                boxShadow:  '0 24px 64px rgba(0,0,0,0.7), 0 0 0 1px rgba(139,92,246,0.1)',
                borderRadius: 16,
                overflow:   'hidden',
            }}
        >
            {/* ── Header ─────────────────────────────────────────────── */}
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 16px 12px',
                borderBottom: '1px solid rgba(255,255,255,0.07)',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Bell size={15} style={{ color: '#f59e0b' }} />
                    <span style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>Notifiche</span>
                    {unread > 0 && (
                        <span style={{
                            fontSize: 10, fontWeight: 800,
                            padding: '2px 6px', borderRadius: 99,
                            background: 'rgba(245,158,11,0.15)',
                            color: '#fbbf24',
                            border: '1px solid rgba(245,158,11,0.25)',
                        }}>
                            {unread} nuove
                        </span>
                    )}
                </div>
                {notifications.length > 0 && (
                    <button
                        onClick={markAllRead}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 4,
                            fontSize: 11, color: '#64748b', background: 'none',
                            border: 'none', cursor: 'pointer', padding: '4px 6px',
                            borderRadius: 6, transition: 'color 0.15s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.color = '#94a3b8'}
                        onMouseLeave={e => e.currentTarget.style.color = '#64748b'}
                    >
                        <CheckCheck size={12} />
                        Tutte lette
                    </button>
                )}
            </div>

            {/* ── Notification list ───────────────────────────────────── */}
            <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                {preview.length === 0 ? (
                    <div style={{ padding: '32px 16px', textAlign: 'center' }}>
                        <Bell size={32} style={{ color: '#1e293b', margin: '0 auto 10px' }} />
                        <p style={{ fontSize: 13, color: '#475569' }}>Nessuna notifica</p>
                    </div>
                ) : (
                    preview.map((n, i) => {
                        const Icon = TYPE_ICON[n.type] || Info;
                        return (
                            <motion.div
                                key={n.id}
                                initial={{ opacity: 0, x: -6 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: i * 0.04 }}
                                onClick={() => {
                                    markOneRead(n.id);
                                    setOpen(false);
                                    if (n.type === 'cte_propagated') {
                                        // Naviga alla pagina notifiche con param per aprire il modal
                                        navigate(`/app/notifications?cte_modal=${encodeURIComponent(n.dbId || n.id)}`);
                                    } else {
                                        navigate(n.link || '/app/notifications');
                                    }
                                }}
                                style={{
                                    display: 'flex', alignItems: 'flex-start', gap: 12,
                                    padding: '11px 16px',
                                    borderBottom: i < preview.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                                    background: !n.read ? 'rgba(255,255,255,0.025)' : 'transparent',
                                    cursor: 'pointer',
                                    transition: 'background 0.12s',
                                    position: 'relative',
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                                onMouseLeave={e => e.currentTarget.style.background = !n.read ? 'rgba(255,255,255,0.025)' : 'transparent'}
                            >
                                {/* Unread dot */}
                                {!n.read && (
                                    <span style={{
                                        position: 'absolute', left: 5,
                                        top: '50%', transform: 'translateY(-50%)',
                                        width: 6, height: 6, borderRadius: '50%',
                                        background: n.color, flexShrink: 0,
                                    }} />
                                )}

                                {/* Icon */}
                                <div style={{
                                    width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                                    background: `${n.color}18`,
                                    border: `1px solid ${n.color}28`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <Icon size={16} style={{ color: n.color }} />
                                </div>

                                {/* Content */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <p style={{
                                        fontSize: 12, fontWeight: n.read ? 500 : 700,
                                        color: n.read ? '#cbd5e1' : '#fff',
                                        lineHeight: 1.3, marginBottom: 2,
                                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                    }}>
                                        {n.title}
                                    </p>
                                    <p style={{
                                        fontSize: 11, color: '#64748b', lineHeight: 1.45,
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                    }}>
                                        {n.message}
                                    </p>
                                    <p style={{ fontSize: 10, color: '#f59e0b', marginTop: 3, fontWeight: 500 }}>
                                        {formatRelTime(n.time)}
                                    </p>
                                </div>
                            </motion.div>
                        );
                    })
                )}
            </div>

            {/* ── Footer: Vedi tutte ──────────────────────────────────── */}
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                <button
                    onClick={() => { setOpen(false); navigate('/app/notifications'); }}
                    style={{
                        width: '100%', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', gap: 6,
                        padding: '12px 16px',
                        background: 'transparent', border: 'none',
                        color: '#8b5cf6', fontSize: 13, fontWeight: 700,
                        cursor: 'pointer', transition: 'background 0.15s',
                        borderRadius: '0 0 16px 16px',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(139,92,246,0.08)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                    Vedi tutte le notifiche
                    <ChevronRight size={14} />
                </button>
            </div>
        </motion.div>
    );

    return (
        <>
            {/* Bell button */}
            <button
                ref={buttonRef}
                onClick={handleToggle}
                className="relative p-2 rounded-xl text-gray-400 hover:text-white hover:bg-white/8 transition-all"
                aria-label="Notifiche"
            >
                <Bell className="w-5 h-5" />
                {unread > 0 && (
                    <motion.span
                        key={unread}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[9px] font-bold text-white"
                        style={{ background: 'linear-gradient(135deg, #f59e0b, #ef4444)', boxShadow: '0 0 8px rgba(245,158,11,0.6)' }}
                    >
                        {unread > 9 ? '9+' : unread}
                    </motion.span>
                )}
            </button>

            {/* Dropdown — portal su body per evitare clipping sidebar */}
            {createPortal(
                <AnimatePresence>
                    {open && dropdown}
                </AnimatePresence>,
                document.body
            )}
        </>
    );
}
